import { supplierApi } from '@/lib/api/supplier.api'
import { createLogger } from '@/lib/logger'
/**
 * Supplier mutation hooks using generic factory with custom invalidation strategies
 * Reduced from 310 lines to ~160 lines (48% reduction)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreatePurchaseOrder,
  CreateSupplier,
  CreateSupplierPaymentTerm,
  CreateSupplierProduct,
  PurchaseOrderWithRelations,
  SupplierPaymentTerm,
  SupplierWithRelations,
  UpdatePurchaseOrder,
  UpdateSupplier,
  UpdateSupplierPaymentTerm,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'
import { createSubResourceMutations } from './createSimpleMutation'

const logger = createLogger('hooks/mutations/useSupplierMutations')

/**
 * Supplier mutations using the factory
 * Custom invalidation to also clear supplier purchase orders on delete
 */
const supplierMutations = createResourceMutations<
  SupplierWithRelations,
  CreateSupplier,
  UpdateSupplier
>({
  resourceName: 'Supplier',
  resourceNameSingular: 'supplier',
  queryKeyFactory: queryKeys.suppliers,
  api: {
    create: (data: CreateSupplier) => supplierApi.createSupplier(data),
    update: (id: string, data: UpdateSupplier) =>
      supplierApi.updateSupplier(id, data),
    updateField: (id: string, field: string, value: unknown) =>
      supplierApi.updateSupplier(id, { [field]: value } as UpdateSupplier),
    delete: async (id: string) => {
      await supplierApi.deleteSupplier(id)
    },
  },
  enableRealtime: false,
})
export const useCreateSupplierMutation = supplierMutations.useCreateMutation
export const useUpdateSupplierMutation = supplierMutations.useUpdateMutation
export const useUpdateSupplierFieldMutation =
  supplierMutations.useUpdateFieldMutation!

// ============================================================================
// PAYMENT TERMS — per-org lookup that backs the payment term dropdown.
// ============================================================================

const paymentTermMutations = createSubResourceMutations<
  SupplierPaymentTerm,
  CreateSupplierPaymentTerm,
  UpdateSupplierPaymentTerm
>({
  resourceName: 'Payment term',
  api: {
    create: data => supplierApi.createPaymentTerm(data),
    update: (id, data) => supplierApi.updatePaymentTerm(id, data),
    delete: async id => {
      await supplierApi.deletePaymentTerm(id)
    },
  },
  primaryKey: () => queryKeys.suppliers.paymentTerms(),
  // Vendor lists/details show the resolved term label, and a netDays edit
  // rewrites the denormalized Supplier.paymentTerms server-side.
  additionalKeys: () => [queryKeys.suppliers.lists(), queryKeys.suppliers.details()],
})

export const useCreateSupplierPaymentTermMutation =
  paymentTermMutations.useCreateMutation
export const useUpdateSupplierPaymentTermMutation =
  paymentTermMutations.useUpdateMutation
export const useDeleteSupplierPaymentTermMutation =
  paymentTermMutations.useDeleteMutation

export function useCreateSupplierProductMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      supplierId,
      data,
    }: {
      supplierId: string
      data: CreateSupplierProduct
    }) => supplierApi.createSupplierProduct(supplierId, data),
    onSuccess: (_product, { supplierId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.suppliers.detail(supplierId),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.lists() })
      toast.success('Product linked successfully')
    },
    onError: error => {
      logger.error('[useCreateSupplierProductMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to link product',
      )
    },
  })
}

/**
 * Custom delete mutation to also invalidate supplier purchase orders
 */
export function useDeleteSupplierMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await supplierApi.deleteSupplier(id)
      return id
    },
    onSuccess: deletedId => {
      queryClient.removeQueries({
        queryKey: queryKeys.suppliers.detail(deletedId),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.lists() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.suppliers.supplierPurchaseOrders(deletedId),
      })
      toast.success('Supplier deleted successfully')
    },
    onError: error => {
      logger.error('[useDeleteSupplierMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to delete supplier',
      )
    },
  })
}

/**
 * Bulk delete suppliers mutation hook
 */
export function useBulkDeleteSuppliersMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => supplierApi.bulkDeleteSuppliers(ids),
    onSuccess: result => {
      toast.success(
        `${result.deletedCount} supplier${result.deletedCount === 1 ? '' : 's'} deleted`,
      )
      // Invalidate suppliers list
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.lists() })
    },
    onError: error => {
      toast.error('Failed to delete suppliers')
      logger.error('[useBulkDeleteSuppliersMutation] Error:', error)
    },
  })
}

/**
 * Purchase order mutations - kept manual due to complex cross-resource invalidation
 */

// Helper to invalidate purchase order related queries
function invalidatePurchaseOrderQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  purchaseOrder: PurchaseOrderWithRelations,
) {
  queryClient.setQueryData(
    queryKeys.suppliers.purchaseOrder(purchaseOrder.id),
    purchaseOrder,
  )
  queryClient.invalidateQueries({
    queryKey: queryKeys.suppliers.purchaseOrders(),
  })

  if (purchaseOrder.supplierId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.suppliers.supplierPurchaseOrders(
        purchaseOrder.supplierId,
      ),
    })
  }
}

export function useCreatePurchaseOrderMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePurchaseOrder) =>
      supplierApi.createPurchaseOrder(data),
    onSuccess: newPurchaseOrder => {
      invalidatePurchaseOrderQueries(queryClient, newPurchaseOrder)
      toast.success('Purchase order created successfully')
    },
    onError: error => {
      logger.error('[useCreatePurchaseOrderMutation] Error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create purchase order',
      )
    },
  })
}

export function useUpdatePurchaseOrderMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePurchaseOrder }) =>
      supplierApi.updatePurchaseOrder(id, data),
    onSuccess: updatedPurchaseOrder => {
      invalidatePurchaseOrderQueries(queryClient, updatedPurchaseOrder)
      toast.success('Purchase order updated successfully')
    },
    onError: error => {
      logger.error('[useUpdatePurchaseOrderMutation] Error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update purchase order',
      )
    },
  })
}

export function useUpdatePurchaseOrderStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      supplierApi.updatePurchaseOrderStatus(id, status),
    onSuccess: updatedPurchaseOrder => {
      invalidatePurchaseOrderQueries(queryClient, updatedPurchaseOrder)
      toast.success('Purchase order status updated successfully')
    },
    onError: error => {
      logger.error('[useUpdatePurchaseOrderStatusMutation] Error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update purchase order status',
      )
    },
  })
}

export function useReceivePurchaseOrderItemsMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      items,
    }: {
      id: string
      items: Array<{ inventoryItemId: string; receivedQty: number }>
    }) => supplierApi.receivePurchaseOrderItems(id, items),
    onSuccess: updatedPurchaseOrder => {
      invalidatePurchaseOrderQueries(queryClient, updatedPurchaseOrder)
      toast.success('Purchase order items received successfully')
    },
    onError: error => {
      logger.error('[useReceivePurchaseOrderItemsMutation] Error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to receive purchase order items',
      )
    },
  })
}

export function useDeletePurchaseOrderMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      supplierId,
    }: {
      id: string
      supplierId?: string
    }) => {
      await supplierApi.deletePurchaseOrder(id)
      return { id, supplierId }
    },
    onSuccess: (_, { id, supplierId }) => {
      queryClient.removeQueries({
        queryKey: queryKeys.suppliers.purchaseOrder(id),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.suppliers.purchaseOrders(),
      })

      if (supplierId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.suppliers.supplierPurchaseOrders(supplierId),
        })
      }

      toast.success('Purchase order deleted successfully')
    },
    onError: error => {
      logger.error('[useDeletePurchaseOrderMutation] Error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to delete purchase order',
      )
    },
  })
}
